import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {
  propertyFieldsToChangedPropertiesMap,
  useEditCustomProperties,
} from '@github-ui/custom-properties-editing/use-edit-custom-properties'
import type {
  PropertyDefinitionBase,
  PropertySourceDetails,
  PropertyTarget,
  PropertyValue,
  PropertyValuesRecord,
} from '@github-ui/custom-properties-types'
import {useMutation} from '@github-ui/react-query'
import sudo from '@github-ui/sudo'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {SearchIcon} from '@primer/octicons-react'
import {Button, TextInput, useConfirm} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useEffect, useMemo, useState} from 'react'

import styles from './PropertiesEditingPage.module.css'
import {ServerErrorFormBanner} from './ServerErrorFormBanner'
import {PropertiesValuesTable} from './values-table/PropertiesValuesTable'

const debounceAnnouncement = debounce(announce, 300)

const DEFAULT_ACTION_LABELS = {save: 'Save', cancel: 'Cancel'}

export function PropertiesEditingPage({
  editingTargets,
  onClose,
  editableProperties,
  definitions,
  onSuccess,
  renderPageHeader,
  actionLabels = DEFAULT_ACTION_LABELS,
  setValuesPath,
  // The source level where said property can be managed, i.e. have values set
  managedBySource,
  propertyValueValidator,
  formValidator,
}: {
  editableProperties: string[]
  editingTargets: PropertyTarget[]
  onClose?: () => void
  definitions: PropertyDefinitionBase[]
  onSuccess: () => void
  renderPageHeader: ({canClose}: {canClose: () => Promise<boolean>}) => React.ReactElement
  actionLabels?: {save?: string; cancel?: string}
  setValuesPath: string
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>
  propertyValueValidator: (propertyName: string, value?: PropertyValue) => Promise<string | false | undefined>
  formValidator?: (
    propertyValuesMap: Record<
      string,
      {
        value?: PropertyValue
      }
    >,
  ) => Record<string, string>
}) {
  const editableDefinitions = useMemo(
    () => definitions.filter(({propertyName}) => editableProperties.includes(propertyName)),
    [definitions, editableProperties],
  )

  const editableDefinitionsSet = useMemo(
    () => new Set((editableDefinitions || []).map((definition: PropertyDefinitionBase) => definition.propertyName)),
    [editableDefinitions],
  )

  const {
    propertyValuesMap,
    setPropertyValue,
    revertPropertyValue,
    discardChanges,
    commitChanges,
    propertyValuesErrors,
  } = useEditCustomProperties({
    propertyValues: editingTargets.map(t => t.properties || {}),
    editableDefinitions,
    propertyValueValidator,
  })

  const [filterText, setFilterText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const confirm = useConfirm()

  const hasUnsavedChanges = useMemo(() => Object.values(propertyValuesMap).some(p => p.changed), [propertyValuesMap])
  useNavigateAwayDiscardConfirmation(hasUnsavedChanges)

  const canClose = async () => {
    if (hasUnsavedChanges) {
      return confirm({
        title: 'Discard changes?',
        content: 'You have unsaved changes. Are you sure you want to discard them?',
        cancelButtonContent: 'Keep editing',
        confirmButtonContent: 'Discard',
        confirmButtonType: 'danger',
      })
    } else {
      return true
    }
  }
  const discardWithConfirmation = async () => {
    if (hasUnsavedChanges) {
      if (await canClose()) {
        discardChanges()
        onClose?.()
      }
    } else {
      onClose?.()
    }
  }

  const saveMutation = useMutation({
    onMutate: async () => {
      if (!(await sudo())) throw new Error('Unauthorized')
    },
    mutationFn: async ({
      targetIds,
      changedProperties,
    }: {
      targetIds: number[]
      changedProperties: PropertyValuesRecord
    }) => {
      const response = await reactFetchJSON(setValuesPath, {
        method: 'POST',
        body: {
          targetIds,
          properties: changedProperties,
        },
      })

      if (response.ok) return

      let error: string | undefined
      try {
        const data = (await response.json()) as {error?: string}
        error = data.error
      } catch {
        // ignore JSON parse errors
      }
      throw new Error(error ?? 'Failed to update properties')
    },
    onSuccess: () => {
      commitChanges()
      onSuccess()
    },
    onError: error => {
      setErrorMessage(error.message)
    },
  })

  const validateForm = () => {
    const errorResults = formValidator?.(propertyValuesMap) || {}
    const isValid = Object.keys(errorResults).length === 0

    if (!isValid) {
      propertyValuesErrors(errorResults)
    }

    return isValid
  }

  const onSubmit = async () => {
    // Clear any previous error here (instead of in onMutate) so we also handle
    // the case where the mutation is never called (e.g. no properties changed).
    // Doing this inside the mutation would treat a no-op as success and trigger
    // onSuccess, which is not desired.
    setErrorMessage('')
    if (saveMutation.isPending) {
      return
    }

    const formIsValid = validateForm()
    if (!formIsValid || Object.values(propertyValuesMap).some(p => !!p.error)) {
      return
    }

    const changedProperties = propertyFieldsToChangedPropertiesMap(definitions, propertyValuesMap)
    if (Object.keys(changedProperties).length) {
      const targetIds = editingTargets.map(target => target.id)
      saveMutation.mutate({targetIds, changedProperties})
    } else {
      onClose?.()
    }
  }

  const filteredDefinitions = definitions.filter(({propertyName}) =>
    propertyName.toLowerCase().includes(filterText.toLowerCase()),
  )

  useEffect(() => {
    const resultsFound = filteredDefinitions.length
    if (resultsFound === 1) {
      debounceAnnouncement('1 result found')
    } else {
      debounceAnnouncement(`${resultsFound} results found`)
    }
  }, [filteredDefinitions])

  if (!editingTargets.length) {
    return null
  }

  return (
    <>
      {errorMessage && (
        <div className={styles.errorContainer}>
          <ServerErrorFormBanner>{errorMessage}</ServerErrorFormBanner>
        </div>
      )}
      {renderPageHeader({canClose})}
      <form data-testid="properties-editing-page" className={styles.propertiesEditingForm}>
        <TextInput
          block
          leadingVisual={SearchIcon}
          placeholder="Filter properties"
          aria-label="Filter properties"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
        />

        {filteredDefinitions.length === 0 ? (
          <Blankslate border>
            <Blankslate.Heading>No properties that match</Blankslate.Heading>
          </Blankslate>
        ) : (
          <PropertiesValuesTable
            definitions={filteredDefinitions}
            propertyValuesMap={propertyValuesMap}
            rowRenderer={(rowProps, setRef) => {
              const {definition} = rowProps
              const isEditable = editableDefinitionsSet.has(definition.propertyName)

              // Temporary solution until this component no longer has a hard dependency on PropertyDefinitionBase
              // by splitting it up by domain
              let required = false
              if ('requireExplicitValues' in definition) {
                required = !!definition.requireExplicitValues
              }

              if (isEditable) {
                return (
                  <PropertiesValuesTable.EditValueRow
                    required={required}
                    setRef={setRef}
                    {...rowProps}
                    onChange={setPropertyValue}
                    onReset={revertPropertyValue}
                    showUndo
                  />
                )
              }
              return (
                <PropertiesValuesTable.ReadValueRow
                  required={required}
                  {...rowProps}
                  managedBySource={managedBySource}
                />
              )
            }}
          />
        )}
        <div className={styles.formActionsContainer}>
          {actionLabels.save && (
            <Button type="button" onClick={onSubmit} variant="primary">
              {actionLabels.save}
            </Button>
          )}

          {actionLabels.cancel && (
            <Button type="button" onClick={discardWithConfirmation} variant="default">
              {actionLabels.cancel}
            </Button>
          )}
        </div>
      </form>
    </>
  )
}

function useNavigateAwayDiscardConfirmation(hasUnsavedChanges: boolean) {
  useEffect(() => {
    document.body.setAttribute('data-turbo', 'false')
    window.onbeforeunload = hasUnsavedChanges
      ? e => {
          e.returnValue = 'You have unsaved changes. Are you sure you want to leave this page?'
        }
      : null

    return () => {
      document.body.removeAttribute('data-turbo')
      window.onbeforeunload = null
    }
  }, [hasUnsavedChanges])
}

try{ PropertiesEditingPage.displayName ||= 'PropertiesEditingPage' } catch {}